/**
 * Программы инспекций по регламенту заказчика.
 *
 * Инспекция проводится через каждый межремонтный интервал, а программа работ
 * идёт повторяющимся циклом из восьми шагов:
 *
 *   интервал, ч×1000 | 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80
 *   программа        | L1 | L2 | L1 | L3 | L1 | L2 | L1 | L4
 *
 * Дальше цикл повторяется: 90 -> L1, 100 -> L2, 110 -> L1, 120 -> L3 и так далее.
 * Поэтому шкала не заканчивается на L4 и работает для генераторов 100 000+ часов.
 */
export const INSPECTION_PROGRAM_CYCLE = ['L1', 'L2', 'L1', 'L3', 'L1', 'L2', 'L1', 'L4'] as const;

/** Шаг цикла по умолчанию, если у машины не задан межремонтный интервал. */
export const DEFAULT_INSPECTION_INTERVAL_HOURS = 10_000;

export type InspectionProgram = typeof INSPECTION_PROGRAM_CYCLE[number];

export interface InspectionStep {
  /** Номер инспекции от начала эксплуатации, с единицы. */
  stepNumber: number;
  hours: number;
  program: InspectionProgram;
}

/** Программа для инспекции с номером stepNumber (нумерация с единицы). */
export const getInspectionProgram = (stepNumber: number): InspectionProgram => (
  INSPECTION_PROGRAM_CYCLE[(stepNumber - 1) % INSPECTION_PROGRAM_CYCLE.length]
);

/** Сколько инспекций показывать за ближайшей предстоящей. */
export const UPCOMING_INSPECTIONS_AHEAD = 4;

/**
 * Инспекции от первой до нескольких за ближайшей предстоящей.
 * Шкала прокручиваемая, поэтому показываем всю историю плюс запас вперёд -
 * так только что наступившая инспекция остаётся на виду, а границы циклов
 * не создают скачков. Минимум один полный цикл, чтобы шкала не была пустой
 * у новой машины.
 */
export const getInspectionSteps = (currentHours: number, intervalHours: number): InspectionStep[] => {
  const nextStepNumber = Math.floor(Math.max(0, currentHours) / intervalHours) + 1;
  const lastStepNumber = Math.max(
    INSPECTION_PROGRAM_CYCLE.length,
    nextStepNumber + UPCOMING_INSPECTIONS_AHEAD,
  );

  return Array.from({ length: lastStepNumber }, (_, index) => {
    const stepNumber = index + 1;
    return {
      stepNumber,
      hours: stepNumber * intervalHours,
      program: getInspectionProgram(stepNumber),
    };
  });
};

/** Ближайшая предстоящая инспекция. */
export const getNextInspection = (currentHours: number, intervalHours: number): InspectionStep => {
  const stepNumber = Math.floor(Math.max(0, currentHours) / intervalHours) + 1;
  return {
    stepNumber,
    hours: stepNumber * intervalHours,
    program: getInspectionProgram(stepNumber),
  };
};
